/* WP-10 i18n (§14.10): i18next, three locales, concierge tone (TONE.md).
 * Key convention <namespace>.<screen>.<element>. */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import zhHant from './locales/zh-hant.json';
import zhHans from './locales/zh-hans.json';

export const PORTAL_LOCALES = ['en', 'zh-hant', 'zh-hans'] as const;
export type PortalLocale = (typeof PORTAL_LOCALES)[number];

const stored = (() => {
  try {
    return localStorage.getItem('hp.locale');
  } catch {
    return null;
  }
})();

const browser = navigator.language.toLowerCase();
const detected: PortalLocale =
  (stored as PortalLocale | null) && PORTAL_LOCALES.includes(stored as PortalLocale)
    ? (stored as PortalLocale)
    : browser.startsWith('zh')
      ? browser.includes('cn') || browser.includes('hans') || browser.includes('sg')
        ? 'zh-hans'
        : 'zh-hant'
      : 'en';

void i18n.use(initReactI18next).init({
  // Resource keys must be the canonical Intl locale codes: i18next looks up
  // translations via the canonicalised chain ("zh-hant" -> "zh-Hant" -> "zh"),
  // so registering under the lowercase app code silently falls back to en.
  resources: {
    en: { translation: en },
    'zh-Hant': { translation: zhHant },
    'zh-Hans': { translation: zhHans },
  },
  lng: detected,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
});

export function setPortalLocale(locale: PortalLocale): void {
  try {
    localStorage.setItem('hp.locale', locale);
  } catch {
    /* ignore */
  }
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale === 'en' ? 'en' : locale === 'zh-hans' ? 'zh-Hans' : 'zh-Hant';
}

export function currentPortalLocale(): PortalLocale {
  const lng = i18n.language;
  return (PORTAL_LOCALES as readonly string[]).includes(lng) ? (lng as PortalLocale) : 'en';
}

export default i18n;
